import { Dispatch, SetStateAction, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { Link, useHistory } from 'react-router-dom';
import { ROUTES } from 'constants/routes';
import { partnerDetails } from 'constants/partnerDetails';
import { useStore } from 'providers';
import { expireSoon } from 'helpers/date';
import { Voucher, VoucherAccordionState } from 'models';
import { sendRevealedVoucher } from 'services/loyaltyGateway';
import { Media, Content } from 'components/Modal';
import TertiaryButton from 'components/TertiaryButton';
import TermsAccordion from 'components/TermsAccordion';
import CodeAccordion from 'components/CodeAccordion';
import ThirdPartyCard from 'components/ThirdPartyCard';
import ThirdPartyInfo from 'components/ThirdPartyInfo';
import ThirdPartyCodeRevealModal from 'views/modals/ThirdPartyCodeReveal';

import {
  Wrapper,
  MediaWrap,
  SurprisePerkFlash,
  SurprisePerkTitle,
  StyledMissingImage,
  StyledVoucherState,
  StyledCodeAccordion,
  StyledAlert,
  Footer,
  ThirdPartyBox,
  StyledImage,
} from './VoucherInformation.styles';

export interface VoucherInformationProps {
  onClose: () => void;
  voucher: Voucher;
  accordions: VoucherAccordionState;
  setAccordions: Dispatch<SetStateAction<VoucherAccordionState>>;
  showFooter?: boolean;
  onNext?: () => void;
}

const VoucherInformation = ({
  onClose,
  voucher,
  accordions,
  setAccordions,
  showFooter = false,
  onNext,
}: VoucherInformationProps) => {
  const { title, imageUrl, date, redemptionCode, conditions, surpriseVoucher, voucherId } = voucher;

  const [revealModalIsOpen, setRevealModalIsOpen] = useState(false);
  const [revealCodeIsLoading, setRevealCodeIsLoading] = useState(false);
  const [revealCodeHasError, setRevealCodeHasError] = useState(false);

  const { store, dispatch } = useStore();
  const { userData } = store;

  const history = useHistory();
  const isSpinToWin = history.location.state?.type === 'spin-to-win';

  const member = {
    id: userData?.personalDetails.memberId || '',
    title: 'Zillionaire ID:',
    text: 'Show your Zillionaire ID to your server to register your visit and bank those Zs.',
  };

  const isAlert = expireSoon(date);
  const successAlert = `${title} is ready to redeem`;

  const isThirdPartyPerk = voucher.voucherType !== 'Standard';
  const thirdPartyPartner = isThirdPartyPerk && partnerDetails[voucher.voucherType];
  const shouldConfirmReveal = isThirdPartyPerk && !voucher.isRevealed;
  const hasConfirmedReveal = thirdPartyPartner && voucher.isRevealed;

  const handleOnAccordionToggle = (key: 'code' | 'id') => {
    setAccordions({ ...accordions, [key]: !accordions[key] });
  };

  const handleOnRevealModalToggle = () => {
    setRevealModalIsOpen(!revealModalIsOpen);
    setRevealCodeHasError(false);
  };

  const handleOnReveal = async () => {
    if (!voucherId) return;

    setRevealCodeHasError(false);
    setRevealCodeIsLoading(true);

    try {
      const newVouchersData = await sendRevealedVoucher(voucherId);

      dispatch({
        type: 'setVouchersData',
        payload: { vouchersData: newVouchersData },
      });

      handleOnRevealModalToggle();
    } catch (error) {
      setRevealCodeHasError(true);
    }

    setRevealCodeIsLoading(false);
  };

  const ThirdPartyPerk = () => {
    if (hasConfirmedReveal) {
      return <ThirdPartyCard code={redemptionCode} />;
    }

    return <ThirdPartyInfo />;
  };

  const CallToAction = () => {
    const buttonProps = {
      fullWidth: true,
      color: 'primary',
      component: Link,
      to: ROUTES.PERKZ_WALLET,
      onClick: onClose,
    };

    if (shouldConfirmReveal) {
      return (
        <Footer>
          <Button fullWidth variant="contained" onClick={handleOnRevealModalToggle}>
            Reveal code
          </Button>
          <TertiaryButton {...buttonProps}>Save for later</TertiaryButton>
        </Footer>
      );
    }

    if (hasConfirmedReveal) {
      return (
        <Footer>
          <Button fullWidth variant="contained" href={thirdPartyPartner.url} target="_blank">
            Take me to {thirdPartyPartner.label}
          </Button>
        </Footer>
      );
    }

    if (isSpinToWin) {
      return (
        <Footer>
          <Button onClick={onClose} fullWidth variant="contained">
            Continue to Zillionaires&apos; Club home
          </Button>
        </Footer>
      );
    }

    return (
      <Footer>
        <StyledAlert severity="success" icon={<CheckCircle />}>
          {successAlert}
        </StyledAlert>
        <Button onClick={onNext || onClose} fullWidth variant="contained">
          Get more Perkz
        </Button>
        <TertiaryButton {...buttonProps}>View your redeemable Perkz</TertiaryButton>
      </Footer>
    );
  };

  return (
    <Wrapper>
      <MediaWrap>
        {surpriseVoucher && (
          <SurprisePerkFlash>
            <SurprisePerkTitle variant="h2">Surprise Perk</SurprisePerkTitle>
          </SurprisePerkFlash>
        )}

        {thirdPartyPartner && (
          <ThirdPartyBox aria-label="Third Party Logo">
            <StyledImage src={thirdPartyPartner.logo} />
          </ThirdPartyBox>
        )}

        {imageUrl ? <Media image={imageUrl} /> : <StyledMissingImage />}
      </MediaWrap>

      <Content>
        <Typography variant="h2" aria-label="Perk Title">
          {title}
        </Typography>

        {date && <StyledVoucherState date={date} showDate />}

        {isThirdPartyPerk ? (
          <ThirdPartyPerk />
        ) : (
          <>
            <StyledCodeAccordion
              code={redemptionCode}
              title="Redemption Code:"
              text={`Show this code to a member of our team to redeem your ${title}`}
              qrCaption={`${member.title} ${member.id}`}
              isAlert={isAlert}
              isOpen={accordions.code}
              onClick={() => handleOnAccordionToggle('code')}
            />

            <CodeAccordion
              code={member.id}
              title={member.title}
              text={member.text}
              isOpen={accordions.id}
              onClick={() => handleOnAccordionToggle('id')}
            />
          </>
        )}

        {conditions && <TermsAccordion offerTitle={title} conditions={conditions} />}

        {(showFooter || isThirdPartyPerk) && <CallToAction />}
      </Content>

      <ThirdPartyCodeRevealModal
        isOpen={revealModalIsOpen}
        isLoading={revealCodeIsLoading}
        hasError={revealCodeHasError}
        onClose={handleOnRevealModalToggle}
        onReveal={handleOnReveal}
      />
    </Wrapper>
  );
};

export default VoucherInformation;
